import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, User, Edit, UserCircle, Star, Building2, Headphones, Receipt, Send, Mail, Network, ChevronDown, ChevronRight } from "lucide-react";
import type { UserRole } from "@shared/schema";
import MyProfileEditor from "@/components/my-profile-editor";
import ProfileDetailView from "@/components/profile-detail-view";
import { SquareCropDialog } from "@/components/square-crop-dialog";
import { useProfilePictureUpload } from "@/hooks/use-profile-picture-upload";
import { apiRequest } from "@/lib/queryClient";
import { getAvatarInitials, getAvatarSource } from "@/lib/avatar";
import { useToast } from "@/hooks/use-toast";

interface ProfileEntry {
  id: string;
  type: "personal" | "talent";
  firstName?: string;
  lastName?: string;
  displayName: string;
  title: string | null;
  bioShort: string | null;
  photoUrl: string | null;
  role: string;
  isPublished: boolean;
  hasProfile?: boolean;
  linkedUserId?: string;
}

interface ProfilesDirectory {
  admin: ProfileEntry[];
  sales: ProfileEntry[];
  billing: ProfileEntry[];
  artists: ProfileEntry[];
}

interface ProfilesPageProps {
  effectiveRole: UserRole;
  effectiveUserId: string;
}

// Task #702 — read-only org chart rendered from /api/workspace/org-chart.
// Tree is built client-side from the flat (id, reportsToUserId) edges.
// Roots = anyone with no manager (reportsToUserId === null). Departments
// are shown as a small chip; managers get a "Mgr" pill. Cycles are
// blocked at write-time so we don't need cycle-defense here, but we cap
// recursion depth defensively.
const DEPARTMENT_CHIP_LABELS: Record<string, string> = {
  sales: "Sales",
  marketing: "Marketing",
  talent: "Talent",
  billing: "Billing",
  ops: "Ops",
};

interface OrgChartUser {
  id: string;
  displayName: string;
  role: string | null;
  departmentId: string;
  reportsToUserId: string | null;
  isManager: boolean;
}

interface OrgChartResponse {
  users: OrgChartUser[];
  departments: string[];
}

function OrgChartNode({
  node,
  childrenById,
  depth,
  expanded,
  onToggle,
  onPick,
}: {
  node: OrgChartUser;
  childrenById: Map<string, OrgChartUser[]>;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onPick: (userId: string) => void;
}) {
  const children = childrenById.get(node.id) || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.id);
  return (
    <div className="flex flex-col gap-2" data-testid={`org-node-${node.id}`}>
      <div
        className="flex items-center gap-2 rounded-md border bg-card px-2 py-2 hover-elevate"
        style={{ marginLeft: depth * 24 }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="h-5 w-5 inline-flex items-center justify-center rounded hover:bg-muted/60 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            aria-label={isOpen ? "Collapse" : "Expand"}
            data-testid={`button-org-toggle-${node.id}`}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" />
        )}
        <button
          type="button"
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => onPick(node.id)}
          data-testid={`button-org-open-${node.id}`}
        >
          <UserCircle className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium text-sm">{node.displayName}</span>
          <Badge variant="outline" className="text-xs">
            {DEPARTMENT_CHIP_LABELS[node.departmentId] || node.departmentId}
          </Badge>
          {node.isManager && (
            <Badge variant="secondary" className="text-xs">
              Mgr
            </Badge>
          )}
        </button>
      </div>
      {hasChildren && isOpen && depth < 32 && (
        <div className="flex flex-col gap-2 border-l border-dashed border-border ml-3 pl-2">
          {children.map((c) => (
            <OrgChartNode
              key={c.id}
              node={c}
              childrenById={childrenById}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onPick={onPick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgChartView({
  directory,
  onPickProfile,
}: {
  directory: ProfilesDirectory | undefined;
  onPickProfile: (entry: ProfileEntry) => void;
}) {
  const { data, isLoading } = useQuery<OrgChartResponse>({
    queryKey: ["/api/workspace/org-chart"],
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Auto-expand roots on first load so the tree isn't a wall of leaves.
  const users = data?.users || [];
  const idSet = new Set(users.map((u) => u.id));
  const childrenById = new Map<string, OrgChartUser[]>();
  for (const u of users) {
    if (!u.reportsToUserId) continue;
    const arr = childrenById.get(u.reportsToUserId) || [];
    arr.push(u);
    childrenById.set(u.reportsToUserId, arr);
  }
  const roots = users.filter(
    (u) => !u.reportsToUserId || !idSet.has(u.reportsToUserId),
  );
  // First-render expansion: open every root. Subsequent renders keep
  // whatever the user has toggled.
  const [didInit, setDidInit] = useState(false);
  if (!didInit && users.length > 0) {
    const initial = new Set<string>();
    for (const r of roots) initial.add(r.id);
    setExpanded(initial);
    setDidInit(true);
  }

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Map a workspace userId back to a ProfileEntry so the existing
  // profile detail dialog can open. We try the personal profile first,
  // then linked talent profiles. If no published profile exists yet,
  // we fall back to a synthetic stub so the dialog still renders the
  // header (the existing detail view tolerates missing fields).
  const handlePick = (userId: string) => {
    const entry = findProfileForUser(userId, directory);
    if (entry) onPickProfile(entry);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (users.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No workspace members yet.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="org-chart-tree">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Network className="h-4 w-4" />
          <span>
            Read-only view of who reports to whom. Click a person to open their
            profile. Admins manage assignments in Admin → Users.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(new Set(users.map((u) => u.id)))}
            data-testid="button-org-expand-all"
          >
            Expand all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(new Set())}
            data-testid="button-org-collapse-all"
          >
            Collapse all
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {roots.map((r) => (
          <OrgChartNode
            key={r.id}
            node={r}
            childrenById={childrenById}
            depth={0}
            expanded={expanded}
            onToggle={toggle}
            onPick={handlePick}
          />
        ))}
      </div>
    </div>
  );
}

function findProfileForUser(
  userId: string,
  directory: ProfilesDirectory | undefined,
): ProfileEntry | null {
  if (!directory) return null;
  const groups = [
    directory.admin,
    directory.sales,
    directory.billing,
    directory.artists,
  ];
  for (const g of groups) {
    for (const p of g) {
      if (p.linkedUserId === userId || p.id === userId) return p;
    }
  }
  return null;
}

export default function ProfilesPage({ effectiveRole, effectiveUserId }: ProfilesPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfile, setSelectedProfile] = useState<ProfileEntry | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [nudgingUserId, setNudgingUserId] = useState<string | null>(null);
  const isAdmin = effectiveRole === "admin";
  const { toast } = useToast();

  const {
    pickFile,
    fileInputProps,
    selectedFile,
    cropOpen,
    cancelCrop,
    confirmCrop,
    isUploading: isUploadingPhoto,
  } = useProfilePictureUpload();

  const { data: directory, isLoading, refetch } = useQuery<ProfilesDirectory>({
    queryKey: ["/api/profiles/directory", effectiveRole],
    queryFn: async () => {
      const response = await fetch(`/api/profiles/directory?effectiveRole=${effectiveRole}`);
      if (!response.ok) throw new Error("Failed to fetch profiles");
      return response.json();
    },
  });

  const nudgeMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest("POST", `/api/profiles/nudge/${userId}`);
    },
    onSuccess: () => {
      toast({
        title: "Nudge sent",
        description: "An email reminder has been sent to fill out their profile.",
      });
      setNudgingUserId(null);
    },
    onError: (error: any) => {
      const message = error?.message || "Could not send the email reminder. Please try again.";
      toast({
        title: "Failed to send nudge",
        description: message,
        variant: "destructive",
      });
      setNudgingUserId(null);
    },
  });

  const handleNudge = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    setNudgingUserId(userId);
    nudgeMutation.mutate(userId);
  };

  const filterProfiles = (profiles: ProfileEntry[]): ProfileEntry[] => {
    if (!searchQuery.trim()) return profiles;
    const query = searchQuery.toLowerCase();
    return profiles.filter(
      (p) =>
        p.displayName.toLowerCase().includes(query) ||
        p.title?.toLowerCase().includes(query) ||
        p.bioShort?.toLowerCase().includes(query)
    );
  };

  const getInitials = (name: string) => getAvatarInitials(name);

  const getRoleIcon = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return <Star className="h-3 w-3" />;
      case "billing":
        return <Receipt className="h-3 w-3" />;
      case "artist":
        return <Headphones className="h-3 w-3" />;
      case "marketing":
        return <Mail className="h-3 w-3" />;
      default:
        return <Building2 className="h-3 w-3" />;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role.toLowerCase()) {
      case "admin":
        return "default";
      case "artist":
      case "marketing":
        return "secondary";
      default:
        return "outline";
    }
  };

  const ProfileCard = ({ profile }: { profile: ProfileEntry }) => (
    <Card
      className="p-4 hover-elevate cursor-pointer transition-all"
      onClick={() => setSelectedProfile(profile)}
      data-testid={`profile-card-${profile.id}`}
    >
      <div className="flex items-start gap-4">
        <Avatar className="h-16 w-16 border-2 border-border">
          <AvatarImage src={getAvatarSource(profile)} alt={profile.displayName} />
          <AvatarFallback className="text-lg bg-muted">
            {getInitials(profile.displayName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground truncate">{profile.displayName}</h3>
            {!profile.isPublished && isAdmin && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Unpublished
              </Badge>
            )}
          </div>
          {profile.title && (
            <p className="text-sm text-muted-foreground truncate">{profile.title}</p>
          )}
          {profile.bioShort && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{profile.bioShort}</p>
          )}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant={getRoleBadgeVariant(profile.role)} className="text-xs gap-1">
              {getRoleIcon(profile.role)}
              {profile.role === "sales_rep" ? "Sales" : profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
            </Badge>
            {!profile.isPublished && isAdmin && profile.type === "personal" && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs gap-1"
                onClick={(e) => handleNudge(e, profile.id)}
                disabled={nudgingUserId === profile.id}
                data-testid={`button-nudge-${profile.id}`}
              >
                {nudgingUserId === profile.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
                Nudge
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );

  const ProfileSection = ({
    title,
    icon,
    profiles,
    emptyMessage,
  }: {
    title: string;
    icon: React.ReactNode;
    profiles: ProfileEntry[];
    emptyMessage: string;
  }) => {
    const filtered = filterProfiles(profiles);
    if (filtered.length === 0 && !isAdmin) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold">{title}</h2>
          <Badge variant="secondary" className="ml-2">
            {filtered.length}
          </Badge>
        </div>
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4 text-center">
            {emptyMessage}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((profile) => (
              <ProfileCard key={`${profile.type}-${profile.id}`} profile={profile} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAnyProfiles =
    (directory?.admin?.length || 0) +
      (directory?.sales?.length || 0) +
      (directory?.billing?.length || 0) +
      (directory?.artists?.length || 0) >
    0;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-auto h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
            <UserCircle className="h-7 w-7" />
            Profiles
          </h1>
          <p className="text-sm text-muted-foreground">
            Team directory and professional profiles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full sm:w-64"
              data-testid="input-search-profiles"
            />
          </div>
          <Button onClick={() => setShowEditor(true)} data-testid="button-edit-my-profile">
            <Edit className="h-4 w-4 mr-2" />
            My Profile
          </Button>
        </div>
      </div>

      {!hasAnyProfiles ? (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground bg-card border border-card-border rounded-lg">
          <User className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">No profiles yet</p>
          <p className="text-sm">Team members will appear here once they create and publish their profiles.</p>
          <Button className="mt-4" onClick={() => setShowEditor(true)}>
            Create Your Profile
          </Button>
        </div>
      ) : (
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="admin" data-testid="tab-admin">Admin</TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="artists" data-testid="tab-artists">Artists</TabsTrigger>
            <TabsTrigger value="org-chart" data-testid="tab-org-chart">Org Chart</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-8">
            <ProfileSection
              title="Leadership"
              icon={<Star className="h-5 w-5 text-amber-500" />}
              profiles={directory?.admin || []}
              emptyMessage="No admin profiles published yet"
            />
            <ProfileSection
              title="Sales Team"
              icon={<Building2 className="h-5 w-5 text-blue-500" />}
              profiles={directory?.sales || []}
              emptyMessage="No sales profiles published yet"
            />
            <ProfileSection
              title="Billing Team"
              icon={<Receipt className="h-5 w-5 text-green-500" />}
              profiles={directory?.billing || []}
              emptyMessage="No billing profiles published yet"
            />
            <ProfileSection
              title="Artists & Talent"
              icon={<Headphones className="h-5 w-5 text-purple-500" />}
              profiles={directory?.artists || []}
              emptyMessage="No talent profiles active yet"
            />
          </TabsContent>

          <TabsContent value="admin">
            <ProfileSection
              title="Leadership"
              icon={<Star className="h-5 w-5 text-amber-500" />}
              profiles={directory?.admin || []}
              emptyMessage="No admin profiles published yet"
            />
          </TabsContent>

          <TabsContent value="sales">
            <ProfileSection
              title="Sales Team"
              icon={<Building2 className="h-5 w-5 text-blue-500" />}
              profiles={directory?.sales || []}
              emptyMessage="No sales profiles published yet"
            />
          </TabsContent>

          <TabsContent value="billing">
            <ProfileSection
              title="Billing Team"
              icon={<Receipt className="h-5 w-5 text-green-500" />}
              profiles={directory?.billing || []}
              emptyMessage="No billing profiles published yet"
            />
          </TabsContent>

          <TabsContent value="artists">
            <ProfileSection
              title="Artists & Talent"
              icon={<Headphones className="h-5 w-5 text-purple-500" />}
              profiles={directory?.artists || []}
              emptyMessage="No talent profiles active yet"
            />
          </TabsContent>

          <TabsContent value="org-chart">
            <OrgChartView
              directory={directory}
              onPickProfile={(p) => setSelectedProfile(p)}
            />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!selectedProfile} onOpenChange={(open) => !open && setSelectedProfile(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Profile Details</DialogTitle>
          </DialogHeader>
          {selectedProfile && (
            <ProfileDetailView
              profile={selectedProfile}
              isAdmin={isAdmin}
              currentUserId={effectiveUserId}
              onClose={() => setSelectedProfile(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit My Profile</DialogTitle>
          </DialogHeader>
          <MyProfileEditor
            onClose={() => {
              setShowEditor(false);
              refetch();
            }}
            pickFile={pickFile}
            fileInputProps={fileInputProps}
            isUploading={isUploadingPhoto}
          />
        </DialogContent>
      </Dialog>

      <SquareCropDialog
        open={cropOpen}
        file={selectedFile}
        onCancel={cancelCrop}
        onConfirm={confirmCrop}
        isUploading={isUploadingPhoto}
      />
    </div>
  );
}
